import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import DashboardLayout from "../../../components/layout/DashboardLayout";
import AceAvatar from "../../../components/shared/AceAvatar";

import {
  CONTENT_CATEGORIES,
  CONTENT_STATUS,
  createChallenge,
  fetchChallenges,
} from "../../../services/contentService";

function StatusPill({ status }) {
  const map = {
    [CONTENT_STATUS.PUBLISHED]: "is-published",
    [CONTENT_STATUS.REVIEW]: "is-review",
    [CONTENT_STATUS.DRAFT]: "is-draft",
  };

  return (
    <span className={`ac-content-statuspill ${map[status] || ""}`}>
      {status}
    </span>
  );
}

const DURATION_OPTIONS = ["1 min", "2 min", "5 min", "10 min", "15 min", "20 min", "25 min", "30 min"];

function ChallengePreview({ form }) {
  return (
    <div className="ac-soft-panel ac-content-previewcard">
      <div className="ac-content-block-title">Vorschau</div>

      <div className="mt-4">
        <div className="ac-content-preview-title">
          {form.title || "Challenge-Vorschau"}
        </div>
        <div className="ac-content-preview-meta">
          {form.category} · {form.duration} · {form.status}
        </div>
        <div className="ac-content-preview-text">
          {form.description || "Hier erscheint die Beschreibung der Challenge."}
        </div>
      </div>
    </div>
  );
}

function CreateChallengeForm({ onCancel, onSave }) {
  const [form, setForm] = useState({
    title: "",
    category: "Fokus",
    duration: "5 min",
    status: CONTENT_STATUS.DRAFT,
    description: "",
  });

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };


  return (
    <div className="ac-soft-panel ac-content-formwrap">
      <div className="ac-content-block-head">
        <div>
          <div className="ac-content-block-title">Neue Challenge erstellen</div>
          <div className="ac-content-block-subtitle">
            Lege eine neue Challenge mit sinnvoller Dauer und klarem Ziel an.
          </div>
        </div>

        <AceAvatar variant="motiviert2" size={40} />
      </div>

      <div className="ac-content-formgrid">
        <div className="ac-content-form-main">
          <div className="ac-content-field">
            <label className="ac-content-label">Titel</label>
            <input
              value={form.title}
              onChange={(e) => handleChange("title", e.target.value)}
              placeholder="z. B. 5 Minuten Fokus"
              className="ac-content-input"
            />
          </div>

          <div className="ac-content-form-2col">
            <div className="ac-content-field">
              <label className="ac-content-label">Kategorie</label>
              <select
                value={form.category}
                onChange={(e) => handleChange("category", e.target.value)}
                className="ac-select w-full"
              >
                {CONTENT_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="ac-content-field">
              <label className="ac-content-label">Dauer</label>
              <select
                value={form.duration}
                onChange={(e) => handleChange("duration", e.target.value)}
                className="ac-select w-full"
              >
                {DURATION_OPTIONS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="ac-content-field">
            <label className="ac-content-label">Status</label>
            <select
              value={form.status}
              onChange={(e) => handleChange("status", e.target.value)}
              className="ac-select w-full"
            >
              <option value={CONTENT_STATUS.DRAFT}>{CONTENT_STATUS.DRAFT}</option>
              <option value={CONTENT_STATUS.REVIEW}>{CONTENT_STATUS.REVIEW}</option>
              <option value={CONTENT_STATUS.PUBLISHED}>{CONTENT_STATUS.PUBLISHED}</option>
            </select>
          </div>

          <div className="ac-content-field">
            <label className="ac-content-label">Beschreibung</label>
            <textarea
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={6}
              placeholder="Worum geht es in der Challenge?"
              className="ac-content-textarea"
            />
          </div>
        </div>

        <div className="ac-content-form-side">
          <ChallengePreview form={form} />
        </div>
      </div>

      <div className="ac-content-actions-row">
        <button onClick={onSave} className="ac-primarybtn ac-content-smallbtn">
          Challenge speichern
        </button>
        <button onClick={onCancel} className="ac-ghostbtn ac-content-smallbtn">
          Abbrechen
        </button>
      </div>
    </div>
  );
}

export default function ChallengesListPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [challengeRows, setChallengeRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState("");
  const [cat, setCat] = useState("Alle Kategorien");
  const [status, setStatus] = useState("Alle Status");

  const categories = useMemo(() => ["Alle Kategorien", ...CONTENT_CATEGORIES], []);
  const statuses = ["Alle Status", CONTENT_STATUS.DRAFT, CONTENT_STATUS.REVIEW, CONTENT_STATUS.PUBLISHED];

  const isNew = params.get("new") === "1";

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const data = await fetchChallenges();
        if (!mounted) return;
        setChallengeRows(data);
      } catch (error) {
        console.error("Challenges konnten nicht geladen werden:", error);
        if (!mounted) return;
        setChallengeRows([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    return challengeRows
      .filter((c) =>
        q ? `${c.title} ${c.description} ${c.author}`.toLowerCase().includes(q.toLowerCase()) : true
      )
      .filter((c) => (cat === "Alle Kategorien" ? true : c.category === cat))
      .filter((c) => (status === "Alle Status" ? true : c.status === status));
  }, [challengeRows, q, cat, status]);

  const handleSaveNewChallenge = async (newChallenge) => {
    try {
      await createChallenge(newChallenge);
      const refreshed = await fetchChallenges();
      setChallengeRows(refreshed);
      setParams({});
    } catch (error) {
      console.error("Challenge konnte nicht gespeichert werden:", error);
      alert("Challenge konnte nicht gespeichert werden.");
    }
  };

  return (
    <DashboardLayout title="Inhalte · Challenges">
      <div className="space-y-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="text-xl font-extrabold text-ac-text">Challenges</div>

          <button onClick={() => navigate("/content")} className="ac-ghostbtn">
            ← Übersicht
          </button>
        </div>

        <section className="ac-soft-panel ac-content-hero">
          <div className="ac-content-hero-grid">
            <div>
              <h2 className="ac-content-hero-title">Challenges Liste</h2>
              <p className="ac-content-hero-text">
                Suchen, filtern, öffnen und neue Challenges vorbereiten.
              </p>
            </div>

            <div className="ac-content-hero-side">
              <div className="ac-content-acebox">
                <AceAvatar variant="motiviert2" size={56} />
                {!isNew ? (
                  <button
                    onClick={() => setParams({ new: "1" })}
                    className="ac-primarybtn ac-content-smallbtn"
                  >
                    + Neue Challenge
                  </button>
                ) : (
                  <div className="ac-content-acebubble">
                    Ace sagt: Gute Challenges sind klar, kurz und leicht startbar.
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {isNew && (
          <CreateChallengeForm
            onCancel={() => setParams({})}
            onSave={handleSaveNewChallenge}
          />
        )}

        <section className="ac-soft-panel ac-content-block">
          <div className="ac-content-block-head">
            <div>
              <div className="ac-content-block-title">Suchen & Filtern</div>
              <div className="ac-content-block-subtitle">
                Treffer: {filtered.length}
              </div>
            </div>
          </div>

          <div className="ac-content-filtergrid">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Suche: Titel / Beschreibung / Autor…"
              className="ac-content-input"
            />

            <select
              value={cat}
              onChange={(e) => setCat(e.target.value)}
              className="ac-select w-full"
            >
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>

            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="ac-select w-full"
            >
              {statuses.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </section>

        <section className="ac-soft-panel ac-content-block">
          <div className="ac-content-tablewrap">
            <table className="ac-content-table">
              <thead>
                <tr>
                  <th>Titel</th>
                  <th>Kategorie</th>
                  <th>Dauer</th>
                  <th>Zuletzt bearbeitet von</th>
                  <th>Status</th>
                  <th>Aktion</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6}>
                      <div className="ac-content-empty">Lade Challenges...</div>
                    </td>
                  </tr>
                ) : filtered.length > 0 ? (
                  filtered.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <div className="font-extrabold text-ac-text">{c.title}</div>
                        <div className="text-xs text-ac-text/80">{c.description}</div>
                      </td>
                      <td>{c.category}</td>
                      <td>{c.duration}</td>
                      <td>{c.author}</td>
                      <td>
                        <StatusPill status={c.status} />
                      </td>
                      <td>
                        <button
                          onClick={() => navigate(`/content/challenges/${c.id}`)}
                          className="ac-ghostbtn ac-content-tablebtn"
                        >
                          Öffnen
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6}>
                      <div className="ac-content-empty">
                        Kein Treffer. Versuche andere Filter.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}